import {
  Button,
  ButtonVariants,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  NoteVariant,
  useEventListener,
  useMixpanel,
  useNotes,
} from '@talos/kyoko';
import type React from 'react';
import type { PropsWithChildren, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
  }>;
}

interface AppInstallPromptContextProps {
  installPrompt: BeforeInstallPromptEvent | undefined;
  promptInstall: () => Promise<'accepted' | 'dismissed' | undefined>;
}

const AppInstallPromptContext = createContext<AppInstallPromptContextProps | undefined>(undefined);

const INSTALL_WEB_APP_NOTE_ID = 'install-web-app';

let earlyInstallPrompt: BeforeInstallPromptEvent | undefined;

// Keep this provider mounted as close to the React root as possible. Chrome may
// emit beforeinstallprompt before the authenticated app providers finish loading.
export const AppInstallPromptCaptureProvider: React.FC<{ children: ReactNode }> = ({ children }: PropsWithChildren) => {
  // @ts-expect-error beforeinstallprompt is not a standard event
  useEventListener('beforeinstallprompt', (event: BeforeInstallPromptEvent) => {
    event.preventDefault();
    earlyInstallPrompt = event;
  });

  return <>{children}</>;
};

export const AppInstallPromptProvider: React.FC<{ children: ReactNode }> = ({ children }: PropsWithChildren) => {
  // The prompt provider mounts later because it needs Notes/Mixpanel context.
  // Seed from the root capture provider so Android Chrome prompts are not missed.
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | undefined>(() => earlyInstallPrompt);
  const { addNote, removeNote } = useNotes();
  const mixpanel = useMixpanel();

  // @ts-expect-error beforeinstallprompt is not a standard event
  useEventListener('beforeinstallprompt', (event: BeforeInstallPromptEvent) => {
    setInstallPrompt(event);
    event.preventDefault();
  });

  const promptInstall = useCallback(async () => {
    if (installPrompt == null) {
      return undefined;
    }

    await installPrompt.prompt();
    const choiceResult = await installPrompt.userChoice;

    removeNote(INSTALL_WEB_APP_NOTE_ID);

    if (choiceResult.outcome !== 'accepted') {
      mixpanel.track(MixpanelEvent.WebAppDismissed);
    }

    return choiceResult.outcome;
  }, [installPrompt, removeNote, mixpanel]);

  useEffect(() => {
    if (installPrompt == null) {
      return;
    }
    // Disable until we wan't to make users aware that they can "install" the app
    addNote({
      id: INSTALL_WEB_APP_NOTE_ID,
      text: (
        <>
          <strong>Talos is now available as a standalone web app.</strong> Click install to add it to your desktop
        </>
      ),
      closable: true,
      onDismiss: () => {
        mixpanel.track(MixpanelEvent.WebAppDismissed);
      },
      icon: IconName.DeviceDesktop,
      variant: NoteVariant.Primary,
      actions: (
        <Button size={FormControlSizes.Small} variant={ButtonVariants.Default} onClick={promptInstall}>
          Install
        </Button>
      ),
    });
  }, [installPrompt, promptInstall, addNote, mixpanel]);

  return (
    <AppInstallPromptContext.Provider value={{ installPrompt, promptInstall }}>
      {children}
    </AppInstallPromptContext.Provider>
  );
};

export const useAppInstallPrompt = (): AppInstallPromptContextProps => {
  const context = useContext(AppInstallPromptContext);
  if (!context) {
    throw new Error('useAppInstallPrompt must be used within an AppInstallPromptProvider');
  }
  return context;
};
